import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DeleteForever } from '@material-ui/icons';

import * as Store from '@apply/store';

import * as QS from './question-shared';
import * as QH from './QuestionHelpers';
import { alertModal } from '../modals';

const formAction = Store.FormsStore.Types.formAction;

export interface FileOrFormQuestionProps {
    value?: Store.PropertiesStore.Types.Attachment;
    formTypeName?: string;
    selector: string;
    styleName?: string;
    submitted?: boolean;
    onQuestionValidate?: QS.QuestionValidateType;
    onChange?: (onChangeQualifier: string, newvalue: Store.PropertiesStore.Types.Attachment) => void;
    onChangeQualifier: string;
    onFileSelected: (
        fileType: string,
        file: File,
        updater: (guid: string, value: Store.PropertiesStore.Types.Attachment) => unknown
    ) => Promise<void>;
    fileType?: Store.PropertiesStore.Types.AttachmentType;
    disableEditFormOption?: boolean;
    required: boolean;
}

export const areFilesEqual = (left: QS.OptionalFileUploadValue, right: QS.OptionalFileUploadValue): boolean => {
    if (!left && !right) {
        return true;
    }

    if (!left || !right) {
        return false;
    }

    return (
        left.fileType === right.fileType &&
        left.filename === right.filename &&
        left.provideLater === right.provideLater &&
        left.data === right.data
    );
};

export const isValidValue = (value: Store.PropertiesStore.Types.Attachment): boolean => {
    if (!value || !value.handleMode || value.handleMode === 'unset') {
        return false;
    }
    if (value.handleMode === formAction.upload) {
        return !!value.uploadedFileApiId;
    }

    if (value.handleMode === 'textdata') {
        if (value.data?.trim()?.length < 30) {
            return false;
        }
    }

    return true;
};

export const FileOrFormQuestion: React.FC<FileOrFormQuestionProps> = ({
    selector,
    styleName,
    children,
    value,
    submitted,
    onChange,
    onChangeQualifier,
    onFileSelected,
    fileType,
    formTypeName,
    onQuestionValidate,
    disableEditFormOption,
    required,
}) => {
    const [showValidationMessage, setShowValidationMessage] = useState<boolean>(false);
    const [passing, setPassing] = useState<boolean>(onQuestionValidate === undefined || isValidValue(value));
    const validationLabel = React.useMemo(() => QS.reactNodeToString(children), [children]);

    const [uploading, setUploading] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string>('');
    const [handleMode, setHandleMode] = useState<Store.PropertiesStore.Types.AttachmentHandleMode>(value?.handleMode || 'unset');
    const [textData, setTextData] = useState<string>(value?.data ?? '');
    const validationMessage = useMemo(() => {
        if (handleMode === formAction.upload) {
            return 'Upload required.';
        } else if (handleMode === formAction.textData && textData?.trim()?.length < 1) {
            return 'A response is required.';
        } else if (handleMode === formAction.textData && textData?.trim()?.length < 30) {
            return 'Please provide a little more information.';
        } else {
            return 'A response is required.';
        }
    }, [handleMode, textData]);

    const firstUpdate = useRef(true);

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }
        if (handleMode !== 'textdata' && !!textData) {
            setTextData('');
            return;
        }
        const newvalue = {
            ...Store.PropertiesStore.Types.attachmentInitialState,
            handleMode,
            data: textData,
        };
        setPassing(isValidValue(newvalue));
        onChange(onChangeQualifier, newvalue);
    }, [handleMode, textData, onChange, onChangeQualifier]);

    useEffect(() => {
        setShowValidationMessage(!passing && submitted);
        onQuestionValidate && onQuestionValidate(selector, passing, validationLabel);

        // if this component unmounts, will advise parent that it's valid now, i.e., no longer present
        // and so any prior validation messages should be ignored
        return () => {
            onQuestionValidate && onQuestionValidate(selector, true, validationLabel);
        };
    }, [passing, submitted, selector, onQuestionValidate, validationLabel]);

    const handleModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const val = event.target.value;
        setHandleMode(val as Store.PropertiesStore.Types.AttachmentHandleMode);
    };

    const handleTextDataChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTextData(event.target.value);
    };

    const onRemoveFile = () => {
        onChange(onChangeQualifier, {
            handleMode: formAction.upload,
            uploadedFilename: undefined,
            uploadedFileApiId: undefined,
        });
        setPassing(false);
    };

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setUploading(true);
        try {
            await onFileSelected(fileType, e.target.files[0], onChange);
        } catch (err) {
            setUploadError(err.message);
        }
        setUploading(false);
        setPassing(true);
    };

    useEffect(() => {
        if (uploadError) {
            // todo this could be used to display feedback to user like try again or somesuch
            console.error('file upload error', uploadError);
        }
    }, [uploadError]);

    const computedStyle = {
        ...QS.styles.default,
        ...(styleName ? QS.styles[styleName] : {}),
    };

    const handleClipboardEvent = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const newdata = e.clipboardData.getData('text') ?? '';
        const existingLength = textData?.length ?? 0;
        if (existingLength + newdata.length < 2000) {
            return;
        }

        e.preventDefault();
        const truncated = newdata.substring(0, 2000 - existingLength - 2);
        setTextData((prev) => prev + truncated);
        alertModal({
            message: 'The character limit is 2000 characters, some text has been cut off.',
        });
    };

    return (
        <>
            <fieldset
                id={`fileorform-fieldset-${selector}`}
                data-testid={`fileorform-fieldset-${selector}`}
                style={computedStyle.fieldset}
                className='columns small-12'
            >
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        alignContent: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ flex: 1 }}>
                        <legend
                            id={`fileorform-label-${selector}`}
                            style={{ ...computedStyle.legend }}
                        >
                            {QH.childrenFormatted(children, required)}
                        </legend>
                    </div>

                    <div style={{ marginRight: '10px' }}>
                        <select
                            data-testid={`fileorform-handlemode-${selector}`}
                            style={{ width: '250px' }}
                            value={value?.handleMode || 'unset'}
                            onChange={handleModeChange}
                        >
                            <option value='unset'></option>
                            {!!formTypeName && (
                                <>
                                    <option value={formAction.create}>Create New {formTypeName}</option>
                                    <option
                                        disabled={disableEditFormOption}
                                        value={formAction.edit}
                                    >
                                        Edit Saved {formTypeName}
                                    </option>
                                </>
                            )}
                            <option value={formAction.upload}>Upload File</option>
                            {Store.PropertiesStore.Types.attachmentsSupportingTextData.includes(fileType) && (
                                <option value={formAction.textData}>Enter Text</option>
                            )}
                            <option value={formAction.provideLater}>Provide Later</option>
                            {fileType === 'spec sheet' && <option value={formAction.willNotProvide}>Will Not Provide</option>}
                        </select>
                    </div>

                    <div
                        style={{
                            alignContent: 'flex-end',
                            flex: 2,
                            marginRight: '10px',
                        }}
                    >
                        {value?.handleMode === formAction.upload && !value?.uploadedFileApiId && (
                            <>
                                <input
                                    type='file'
                                    id={`fileorform-file-${selector}`}
                                    data-testid={`fileorform-file-${selector}`}
                                    onChange={handleFileSelect}
                                    style={{
                                        ...computedStyle.input,
                                        maxWidth: '100%',
                                    }}
                                    aria-labelledby={`fileorform-label-${selector}`}
                                />
                            </>
                        )}
                        {value?.handleMode === formAction.upload && !!value?.uploadedFileApiId && (
                            <>
                                <div
                                    style={{
                                        alignContent: 'flex-end',
                                        textAlign: 'right',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                        }}
                                    >
                                        <div style={{ flex: 3 }}>{value?.uploadedFilename}</div>
                                        <div
                                            aria-label='Remove file'
                                            onClick={onRemoveFile}
                                            style={{ cursor: 'pointer' }}
                                            role='button'
                                        >
                                            <DeleteForever />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
                {handleMode === 'textdata' && (
                    <div
                        style={{
                            display: 'flex',
                            width: '100%',
                            marginTop: '10px',
                            alignContent: 'flex-start',
                            justifyContent: 'space-between',
                        }}
                    >
                        <textarea
                            rows={3}
                            maxLength={2000}
                            value={textData}
                            onChange={handleTextDataChange}
                            onPaste={handleClipboardEvent}
                        ></textarea>
                    </div>
                )}
                {showValidationMessage && <QS.ValidationMessage>{validationMessage}</QS.ValidationMessage>}
            </fieldset>
            <div
                data-testid={`file-upload-overlay-${selector}`}
                className='file-upload-overlay'
                style={{ display: uploading ? 'block' : 'none' }}
            >
                <progress></progress>
            </div>
        </>
    );
};
